import { useEffect, useRef, type ReactNode } from "react";

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Read out when the sheet opens. */
  label: string;
  children: ReactNode;
}

/**
 * The phone answer to a side panel (mobile kit Sheet): it rises over the lower part of the
 * screen and leaves the diagram visible behind the scrim. Escape or a tap outside closes it.
 */
export function BottomSheet({ open, onClose, label, children }: BottomSheetProps) {
  const sheet = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    sheet.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div aria-hidden="true" onClick={onClose} className="fixed inset-0 z-40 bg-[rgba(28,63,40,.45)]" />
      <div
        ref={sheet}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="fixed inset-x-0 bottom-0 z-50 max-h-[70dvh] overflow-y-auto rounded-t-2xl bg-surface-card pb-[env(safe-area-inset-bottom)] shadow-xl outline-none"
      >
        <div className="flex justify-center pt-3 pb-1">
          <span className="h-1 w-10 rounded-full bg-ink-200" />
        </div>
        {children}
      </div>
    </>
  );
}
